import React, { useEffect, useState } from "react";
import { View, Text, Platform, StyleSheet } from "react-native";
import moment from "moment";

import ElementsHelper from "../../elements/ElementsHelper";
import cardStyles from "./cardStyles";
import CloseIcon from "../../common/dialog/CloseIcon";
import CardUtils from "./CardUtils";
import StatsCardChart from "./StatsCardChart";
import ReflectionHelper from "../../reflection/ReflectionHelper";
import Utils from "../../../../helpers/utils";
import AppTheme from "../../AppTheme";
import SettingsHelper from "../../more/SettingsHelper";

const DATE_FORMAT = "YYYY-MM-DD";
const MAX_PER_SIDE = 5;

export default function StatsCard({
  controller,
  data = {},
  toggleGoalJournalDialog = () => {},
  reflectionDate = null,
  parentElement = null,
  changePage = null,
  fromReflection = false,
  time_unit = null,
}) {
  if (!controller.state.elements) return null;

  const [showCard, setShowCard] = useState(false);
  const [elementsModel, setElementsModel] = useState({});
  const [moreElements, setMoreElements] = useState([]);
  const [lessElements, setLessElements] = useState([]);
  const [showPercentage, setShowPercentage] = useState(null);

  const userSettings = controller.state.settings || {};

  const cardUtils = new CardUtils(controller, data, setShowCard);

  const timeUnit = time_unit || "week";
  let selectedDate =
    reflectionDate ||
    Utils.nowUserTz(controller).startOf("isoweek").format(DATE_FORMAT);

  if (changePage == null) {
    const day = moment(selectedDate);
    if (
      (day.weekday() == 1 && timeUnit == "week") ||
      (day.date() == 1 && timeUnit == "month") ||
      (day.date() == 1 && day.month() == 1 && timeUnit == "year") ||
      (day.date() == 1 && (day.month() - 1) % 3 == 0 && timeUnit == "quarter")
    ) {
      day.add(-1, timeUnit);
      selectedDate = day.format(DATE_FORMAT);
    }
  }

  // ------------------------------------- METHODS -------------------------------------
  const initElementsModel = (d) => {
    const { delta_hours } = d;

    // Elements + hours
    const elementsHash = {};
    if (delta_hours) {
      delta_hours.forEach((it) => {
        const element = ElementsHelper.findById(controller, it.id_element);
        if (element) {
          elementsHash[it.id_element] = {
            ...element,
            hours: it.hours,
            progress: it.pct,
            totalHours: (it.hours * 100) / it.pct + it.hours,
          };
        }
      });
    }

    return elementsHash;
  };

  const updateGems = (elementsHash, gems) => {
    Object.keys(elementsHash).forEach((elementId) => {
      if (elementsHash[elementId]) {
        elementsHash[elementId].gem = gems[elementId];
      }
    });

    setElementsModel(elementsHash);
    splitElementsList(elementsHash);
  };

  const hasParent = (element, targetElement, elementsById, parsed) => {
    if (element && element.parents && !parsed.includes(element.id)) {
      if (element.parents.includes(targetElement.id)) {
        return true;
      } else {
        const newParsed = [...parsed];
        newParsed.push(element.id);

        for (var i in element.parents) {
          const p = element.parents[i];
          const parentElement = elementsById[p];
          if (parentElement) {
            if (
              hasParent(parentElement, targetElement, elementsById, newParsed)
            ) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };

  const splitElementsList = (elementsHash) => {
    const elementsById = {};
    (controller.state.elements || []).forEach((a) => {
      elementsById[a.id] = a;
    });

    const more = [];
    const less = [];
    Object.values(elementsHash).forEach((el) => {
      const parsed = [];
      const isIncluded =
        parentElement == null ||
        hasParent(el, parentElement, elementsById, parsed);
      if (isIncluded) {
        if (el.hours > 0) {
          if (more.length < MAX_PER_SIDE) {
            more.push(el);
          }
        } else {
          if (less.length < MAX_PER_SIDE) {
            less.push(el);
          }
        }
      }
    });

    setMoreElements(more);
    setLessElements(less);

    if (more.length + less.length == 0) {
      setShowCard(false);
    }
  };

  const togglePercentageView = () => {
    setShowPercentage(!showPercentage);

    SettingsHelper.updatePreferences(
      controller,
      { ...userSettings, show_stats_card_hours: showPercentage },
      () => {
        controller.getItems();
      },
    );
  };

  // ------------------------------------- INIT -------------------------------------
  useEffect(() => {
    setShowPercentage(!userSettings.show_stats_card_hours);
  }, []);

  useEffect(() => {
    cardUtils.restoreCard(data.stored_data);

    const elementsHash = initElementsModel(data);

    ReflectionHelper.getElementGems(
      controller,
      { reflectionDate: selectedDate, time_unit: timeUnit },
      (gems) => updateGems(elementsHash, gems),
    );
  }, [data]);

  const showJournalDialog = (elem) => {
    toggleGoalJournalDialog(
      {
        item_id: elem.id,
        item_type: "activity",
        time_unit: timeUnit,
        name: elem.name,
        icon: elem.icon,
        day: selectedDate,
        gem_id: elem.gem?.gem_id,
        mood: elem.gem?.mood,
        text: elem.gem?.text,
      },
      () => {
        toggleGoalJournalDialog(null);

        // Refresh
        ReflectionHelper.getElementGems(
          controller,
          { reflectionDate: selectedDate, time_unit: timeUnit },
          (gems) => updateGems(elementsModel, gems),
        );
      },
    );
  };

  // ------------------------------------- RENDER -------------------------------------
  return !showCard ? null : (
    <View style={cardStyles.container}>
      <View
        style={[
          cardStyles.card,
          { minHeight: 98, height: "auto" },
          fromReflection && {
            marginHorizontal: 8,
            marginTop: 0,
            paddingTop: 6,
          },
        ]}
      >
        {fromReflection ? (
          <View style={[cardStyles.headerInfo, styles.cardSubTitle]}>
            <Text
              style={styles.title}
            >{`Compared to the ${timeUnit} before:`}</Text>
          </View>
        ) : null}

        {fromReflection ? null : (
          <CloseIcon
            onPress={cardUtils.closeCard}
            style={cardStyles.closeButton}
          />
        )}

        {/* More time spent */}
        <StatsCardChart
          controller={controller}
          elementsList={moreElements}
          selectedDate={selectedDate}
          timeUnit={timeUnit}
          showJournalDialog={showJournalDialog}
          togglePercentageView={togglePercentageView}
          changePage={changePage}
          showPercentage={showPercentage}
        />

        {/* Less time spent */}
        <StatsCardChart
          controller={controller}
          elementsList={lessElements}
          selectedDate={selectedDate}
          timeUnit={timeUnit}
          showPercentage={showPercentage}
          showJournalDialog={showJournalDialog}
          changePage={changePage}
          togglePercentageView={togglePercentageView}
          hasPositive={moreElements.length > 0}
          isNegative
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  title: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: AppTheme.almostBlack,
  },
  cardSubTitle: {
    marginLeft: 6,
  },
});
